import { useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useBodyLock } from "../../hooks/useBodyLock";
import { paymentMethodLabel } from "../../lib/paymentMethods";

export function PaymentMethodPicker({
  methods,
  value,
  onChange,
  labelForMethod,
  isMethodDisabled,
  methodSublabel,
  methodDetails,
}: {
  methods: string[];
  value: string;
  onChange: (method: string) => void;
  labelForMethod?: (method: string) => string;
  isMethodDisabled?: (method: string) => boolean;
  /** Secondary line under the label (e.g. why a method is disabled). */
  methodSublabel?: (method: string) => string | undefined;
  /** Shown only for the currently selected method (e.g. bank transfer instructions). */
  methodDetails?: (method: string) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  useBodyLock(open);

  const label = (m: string) =>
    labelForMethod?.(m) ??
    paymentMethodLabel(m);

  const selectedLabel = value ? label(value) : "Select payment method";

  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!methods.length}
        className="flex w-full items-center gap-3 rounded-2xl border border-white/5 bg-surface px-3.5 py-3.5 text-left text-xs transition-colors active:bg-white/[0.06] disabled:opacity-40"
      >
        <span className="shrink-0 text-white/50">Payment option</span>
        <span className="min-w-0 flex-1 truncate text-right font-semibold text-white">
          {methods.length ? selectedLabel : "None available"}
        </span>
        <ChevronRight size={16} className="shrink-0 text-white/35" aria-hidden />
      </button>

      {value && methodDetails?.(value)}

      <AnimatePresence>
        {open && (
          <motion.div
            key="payment-picker-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[85] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4"
            onClick={() => setOpen(false)}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 28, stiffness: 360 }}
              className="max-h-[min(85dvh,520px)] w-full overflow-hidden rounded-t-2xl border border-white/10 bg-surface shadow-2xl sm:max-w-md sm:rounded-2xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="payment-picker-title"
            >
              <div className="border-b border-white/5 px-4 py-3.5">
                <h2 id="payment-picker-title" className="font-display text-sm font-semibold text-white">
                  Payment option
                </h2>
                <p className="mt-0.5 text-[11px] text-white/45">Choose one method — only its steps show on checkout.</p>
              </div>
              <ul className="max-h-[min(60dvh,400px)] overflow-y-auto p-2">
                {methods.map((m) => {
                  const disabled = isMethodDisabled?.(m) ?? false;
                  const selected = value === m;
                  return (
                    <li key={m}>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          onChange(m);
                          setOpen(false);
                        }}
                        className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-3.5 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                          selected ? "bg-brand-pink/10 text-white" : "text-white/85 active:bg-white/5"
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block font-semibold">{label(m)}</span>
                          {methodSublabel?.(m) ? (
                            <span className="mt-0.5 block text-[10px] font-normal text-white/40">{methodSublabel(m)}</span>
                          ) : null}
                        </span>
                        <span
                          className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                            selected ? "border-brand-pink bg-brand-pink" : "border-white/25"
                          }`}
                          aria-hidden
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="border-t border-white/5 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-full rounded-xl bg-white/5 py-2.5 text-xs font-semibold text-white/80 active:bg-white/10"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
